import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import * as auth from './auth';
import { todayKey } from './format';
import type { DayRow, PaceReport, Walk, WalkSummary } from './models';

/// The web app's view of the walk, mirroring `AppModel` and `MacAppModel`.
/// Nothing derived is computed here - it renders what the API returns.

export interface Store {
  walk: Walk | null;
  walks: Walk[];
  summary: WalkSummary | null;
  pace: PaceReport | null;
  days: DayRow[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  select: (walk: Walk) => Promise<void>;
  setError: (message: string | null) => void;
}

export function useStore(signedIn: boolean): Store {
  const [walk, setWalk] = useState<Walk | null>(null);
  const [walks, setWalks] = useState<Walk[]>([]);
  const [summary, setSummary] = useState<WalkSummary | null>(null);
  const [pace, setPace] = useState<PaceReport | null>(null);
  const [days, setDays] = useState<DayRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWalk = useCallback(async (target: Walk) => {
    const [nextSummary, rows] = await Promise.all([
      api.summary(target.walkId, todayKey()),
      api.days(target.walkId),
    ]);
    setSummary(nextSummary);
    setDays([...rows].reverse());
    // Best-effort: a walk with no intraday readings must not cost us the
    // figures that do exist.
    try {
      setPace(await api.pace(target.walkId, todayKey()));
    } catch {
      setPace(null);
    }
  }, []);

  const reload = useCallback(async () => {
    if (!auth.isSignedIn()) return;
    setLoading(true);
    setError(null);
    try {
      const all = await api.walks();
      setWalks(all);
      const active = all.find((w) => (w.status ?? 'active') === 'active') ?? all[0];
      if (!active) throw new Error('No active walk. Create one before recording steps.');
      setWalk(active);
      await fetchWalk(active);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setLoading(false);
    }
  }, [fetchWalk]);

  const select = useCallback(
    async (target: Walk) => {
      setWalk(target);
      setLoading(true);
      try {
        await fetchWalk(target);
        setError(null);
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : String(failure));
      } finally {
        setLoading(false);
      }
    },
    [fetchWalk],
  );

  useEffect(() => {
    if (signedIn) void reload();
  }, [signedIn, reload]);

  // Poll while the page is visible, so a walk in progress keeps up without
  // waking anything while the tab is in the background.
  useEffect(() => {
    if (!signedIn) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void reload();
    };
    const timer = window.setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [signedIn, reload]);

  return { walk, walks, summary, pace, days, loading, error, reload, select, setError };
}
